import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  Bookmark,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  Flag,
  Home,
  Layers3,
  Search,
  Smartphone,
} from "lucide-react";
import { useEffect, useState } from "react";

const workbenchItems = [
  ["overview", "工作概览"],
  ["todos", "我的待办"],
  ["messages", "消息通知"],
  ["duties", "我的职责"],
  ["licenses", "我的证照"],
  ["calendar", "安全日历"],
  ["shortcuts", "快捷功能配置"],
] as const;

const dashboardItems = [
  ["overview", "安全态势总览"],
  ["business", "业务专题看板"],
  ["roadnet", "路网运行安全"],
  ["weather", "气象安全看板"],
  ["supervision", "预警督办中心"],
  ["management", "看板配置管理"],
] as const;

const riskItems = [
  ["list", "风险点台账"],
  ["hazard", "危险源辨识"],
  ["assessment", "风险评估"],
  ["control", "分级管控"],
  ["notice", "风险告知"],
  ["dynamic", "动态更新"],
  ["stats", "风险统计"],
  ["alerts", "预警关联"],
] as const;

const hazardItems = [
  ["list", "隐患台账"],
  ["rectify", "整改管理"],
  ["accept", "验收销号"],
  ["major", "重大隐患管理"],
  ["external", "外部异常关联"],
  ["stats", "隐患统计"],
] as const;

const inspectionItems = [
  ["plan", "检查计划"],
  ["templates", "检查表模板"],
  ["execution", "检查实施"],
  ["tasks", "检查任务"],
  ["notices", "整改通知书"],
  ["ledger", "检查台账"],
  ["patrol", "路网巡查"],
] as const;

const reportItems = [
  ["submit", "我要报告"],
  ["verify", "报告核查办理"],
  ["reward", "奖励办理"],
  ["supervise", "报告监督"],
  ["stats", "统计分析"],
  ["disclosure", "报告公示"],
] as const;

const responsibilityItems = [
  ["targets", "年度目标"],
  ["tracking", "目标执行跟踪"],
  ["templates", "责任书模板"],
  ["signing", "责任书签署"],
  ["checklist", "履职清单库"],
  ["positions", "岗位职责配置"],
  ["duties", "我的履职填报"],
  ["reviews", "履职审核"],
  ["stats", "履职统计"],
] as const;

const institutionItems = [
  ["checklist", "制度清单"],
  ["regulations", "法律法规库"],
  ["documents", "制度文件"],
  ["learning", "制度宣贯"],
] as const;

const performanceItems = [
  ["indicators", "考核指标"],
  ["implementation", "考核实施"],
  ["results", "考核结果"],
] as const;
const meetingItems = [
  ["ledger", "会议台账"],
  ["stats", "会议统计"],
] as const;
const emergencyItems = [
  ["plans", "应急预案"],
  ["drills", "应急演练"],
  ["resources", "应急资源"],
  ["duty", "应急值守与响应"],
  ["stats", "应急统计"],
] as const;
const accidentItems = [
  ["ledger", "事故台账"],
  ["investigations", "事故调查处理"],
  ["stats", "事故统计"],
  ["roadnet", "路网交通事故"],
] as const;
const trainingItems = [
  ["plans", "培训计划"],
  ["courses", "课程管理"],
  ["questions", "题库管理"],
  ["exams", "在线考试"],
  ["learning", "学习管理"],
  ["archives", "培训档案"],
  ["stats", "培训统计"],
] as const;
const licenseItems = [
  ["enterprise", "企业资质证照"],
  ["personnel", "人员资质证照"],
  ["warnings", "证照预警"],
] as const;
const contractorItems = [
  ["access", "准入管理"],
  ["agreements", "安全协议"],
  ["process", "过程管理"],
  ["performance", "绩效评价"],
] as const;
const investmentItems = [
  ["plans", "投入计划"],
  ["adjustments", "计划调整"],
  ["expenses", "支出填报"],
  ["stats", "统计分析"],
] as const;
const analyticsItems = [
  ["domains", "各业务域统计"],
  ["cross", "综合分析"],
  ["reports", "报表管理"],
] as const;
const archiveItems = [
  ["ledger", "安全档案台账"],
  ["annual", "年度归档"],
  ["records", "归档记录查询"],
  ["diagnosis", "完整性诊断"],
  ["handover", "档案移交"],
] as const;

const organizationItems = [
  ["units", "组织单位"],
  ["departments", "部门管理"],
  ["committee", "安委会架构"],
  ["positions", "岗位体系"],
  ["people", "人员管理"],
  ["permissions", "人员与授权"],
  ["parttime", "兼职管理"],
  ["operations", "组织运维中心"],
] as const;

const configurationItems = [
  ["dictionaries", "业务字典"],
  ["classifications", "标准分类"],
  ["codes", "编码规则"],
  ["workflows", "审批流程编排"],
  ["parameters", "系统参数"],
] as const;

const integrationItems = [
  ["comprehensive", "综合管理平台对接"],
  ["channels", "对接通道管理"],
  ["data", "同步数据查询"],
  ["monitor", "同步运行监控"],
  ["validation", "数据一致性校验"],
] as const;

const submissionItems = [
  ["monitor", "报送运行监控"],
  ["organization", "组织架构报送"],
  ["monthly", "安全生产月报"],
  ["hazards", "危险源报送"],
  ["accidents", "事故信息报送"],
  ["hidden", "隐患信息报送"],
  ["history", "报送历史查询"],
] as const;

function getView(search: string) {
  return new URLSearchParams(search).get("view");
}

export function PlatformLayout() {
  const location = useRouterState({ select: (state) => state.location });
  const path = location.pathname;
  const currentView = getView(location.searchStr);
  const inWorkbench = path === "/workbench";
  const inRisk = path === "/risk";
  const inHazards = path === "/hazards";
  const inInspections = path === "/inspections";
  const inReports = path === "/reports";
  const inResponsibilities = path === "/responsibilities";
  const inInstitutions = path === "/institutions";
  const inPerformance = path === "/performance";
  const inMeetings = path === "/meetings";
  const inEmergency = path === "/emergency";
  const inAccidents = path === "/accidents";
  const inTraining = path === "/training";
  const inLicenses = path === "/licenses";
  const inContractors = path === "/contractors";
  const inInvestment = path === "/investment";
  const inAnalytics = path === "/analytics";
  const inArchives = path === "/archives";
  const inDashboard = path === "/dashboards";
  const inOrganization = path === "/organization";
  const inConfiguration = path === "/configuration";
  const inIntegrations = path === "/integrations";
  const inSubmissions = path === "/submissions";
  const inWorkbenchDomain = inWorkbench || inDashboard;
  const inOrganizationDomain = inOrganization || inConfiguration;
  const inIntegrationDomain = inIntegrations || inSubmissions;
  const inEmergencyDomain = inEmergency || inAccidents;
  const inPersonnelDomain =
    inTraining || inLicenses || inContractors || inInvestment;
  const inResponsibilityDomain =
    inResponsibilities || inInstitutions || inPerformance || inMeetings;
  const inPrevention = inRisk || inHazards || inInspections || inReports;
  const inAnalyticsDomain = inAnalytics || inArchives;
  const [openDomains, setOpenDomains] = useState<Set<string>>(
    () =>
      new Set([
        inWorkbenchDomain
          ? "workbench"
          : inPrevention
            ? "prevention"
            : inResponsibilityDomain
              ? "responsibility"
              : inEmergencyDomain
                ? "emergency"
                : inPersonnelDomain
                  ? "training"
                  : inAnalyticsDomain
                    ? "analytics"
                    : inOrganizationDomain
                      ? "organization"
                      : inIntegrationDomain
                        ? "connectors"
                        : "workbench",
      ]),
  );

  useEffect(() => {
    const activeDomain = inWorkbenchDomain
      ? "workbench"
      : inPrevention
        ? "prevention"
        : inResponsibilityDomain
          ? "responsibility"
          : inEmergencyDomain
            ? "emergency"
            : inPersonnelDomain
              ? "training"
              : inAnalyticsDomain
                ? "analytics"
                : inOrganizationDomain
                  ? "organization"
                  : inIntegrationDomain
                    ? "connectors"
                    : null;
    if (!activeDomain) return;
    setOpenDomains((current) => {
      if (current.has(activeDomain)) return current;
      const next = new Set(current);
      next.add(activeDomain);
      return next;
    });
  }, [
    inAnalyticsDomain,
    inEmergencyDomain,
    inPersonnelDomain,
    inPrevention,
    inResponsibilityDomain,
    inWorkbenchDomain,
    inOrganizationDomain,
    inIntegrationDomain,
  ]);

  function toggleDomain(id: string) {
    const next = new Set(openDomains);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenDomains(next);
  }

  return (
    <div className="platform-shell">
      <div className="ambient ambient--top" />
      <div className="ambient ambient--bottom" />
      <header className="topbar">
        <Link className="brand" to="/workbench" search={{ view: "overview" }}>
          <span className="brand__mark">
            <img src="/assets/safety-platform-logo-v2.png" alt="华越高速安全监管平台标志" />
          </span>
          <strong>华越高速安全监管平台</strong>
        </Link>
        <button className="org-switch" type="button">
          <Layers3 size={15} /> 集团本部 <ChevronDown size={13} />
        </button>
        <label className="global-search">
          <Search size={16} />
          <input
            aria-label="全局搜索"
            placeholder={
              inInspections
                ? "搜索检查任务、通知书、隐患…"
                : "搜索风险点、隐患、制度…"
            }
          />
          <kbd>Ctrl K</kbd>
        </label>
        <Link
          className="mobile-entry"
          to="/mobile/workbench"
          search={{ view: "overview" }}
          target="_blank"
        >
          <span><Smartphone size={17} /></span>
          <span>
            <strong>移动端</strong>
            <small>现场作业入口</small>
          </span>
        </Link>
        <span className="topbar__spacer" />
        <Link
          aria-label="我的待办"
          className="top-action"
          to="/workbench"
          search={{ view: "todos" }}
        >
          <CheckCircle2 size={19} />
          <span className="badge badge--red">13</span>
        </Link>
        <Link
          aria-label="消息通知"
          className="top-action"
          to="/workbench"
          search={{ view: "messages" }}
        >
          <Bell size={19} />
          <span className="badge badge--orange">5</span>
        </Link>
        <div className="user-summary">
          <span className="avatar">张</span>
          <span>
            <strong>张明</strong>
            <small>安全监察岗 · 集团安监部</small>
          </span>
          <ChevronDown size={13} />
        </div>
      </header>

      <div className="platform-body">
        <aside className="sidebar">
          <nav className="sidebar__nav" aria-label="平台主导航">
            <div
              className={`nav-group ${inWorkbenchDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("workbench")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("workbench")}
              >
                <Home size={17} />
                <span>工作台</span>
                {openDomains.has("workbench") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("workbench") ? (
                <div className="nav-module">
                  <Link
                    className={`nav-module__title ${inWorkbench ? "is-open" : ""}`}
                    to="/workbench"
                    search={{ view: "overview" }}
                  >
                    个人工作台 <ChevronRight size={12} />
                  </Link>
                  {inWorkbench
                    ? workbenchItems.map(([view, label]) => (
                        <Link
                          className={`nav-page ${currentView === view || (!currentView && view === "overview") ? "is-current" : ""}`}
                          key={view}
                          to="/workbench"
                          search={{ view }}
                        >
                          <span />
                          {label}
                        </Link>
                      ))
                    : null}
                  <Link
                    className={`nav-module__title ${inDashboard ? "is-open" : ""}`}
                    to="/dashboards"
                    search={{ view: "overview" }}
                  >
                    决策看板 <ChevronRight size={12} />
                  </Link>
                  {inDashboard
                    ? dashboardItems.map(([view, label]) => (
                        <Link
                          className={`nav-page ${currentView === view || (!currentView && view === "overview") ? "is-current" : ""}`}
                          key={view}
                          to="/dashboards"
                          search={{ view }}
                        >
                          <span />
                          {label}
                        </Link>
                      ))
                    : null}
                </div>
              ) : null}
            </div>

            <div className={`nav-group ${inPrevention ? "is-active" : ""}`}>
              <button
                aria-expanded={openDomains.has("prevention")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("prevention")}
              >
                <AlertTriangle size={17} />
                <span>双重预防</span>
                {openDomains.has("prevention") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("prevention") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inRisk ? "is-open" : ""}`}
                      to="/risk"
                      search={{ view: "list" }}
                    >
                      风险管理 <ChevronRight size={12} />
                    </Link>
                    {inRisk
                      ? riskItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "list") ? "is-current" : ""}`}
                            key={view}
                            to="/risk"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inHazards ? "is-open" : ""}`}
                      to="/hazards"
                      search={{ view: "list" }}
                    >
                      隐患管理 <ChevronRight size={12} />
                    </Link>
                    {inHazards
                      ? hazardItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "list") ? "is-current" : ""}`}
                            key={view}
                            to="/hazards"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inInspections ? "is-open" : ""}`}
                      to="/inspections"
                      search={{ view: "plan" }}
                    >
                      安全检查 <ChevronRight size={12} />
                    </Link>
                    {inInspections
                      ? inspectionItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "plan") ? "is-current" : ""}`}
                            key={view}
                            to="/inspections"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inReports ? "is-open" : ""}`}
                      to="/reports"
                      search={{ view: "submit" }}
                    >
                      隐患报告（全员） <ChevronRight size={12} />
                    </Link>
                    {inReports
                      ? reportItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "submit") ? "is-current" : ""}`}
                            key={view}
                            to="/reports"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>

            <div
              className={`nav-group ${inResponsibilityDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("responsibility")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("responsibility")}
              >
                <FileText size={17} />
                <span>责任与制度</span>
                {openDomains.has("responsibility") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("responsibility") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inResponsibilities ? "is-open" : ""}`}
                      to="/responsibilities"
                      search={{ view: "targets" }}
                    >
                      目标职责管理 <ChevronRight size={12} />
                    </Link>
                    {inResponsibilities
                      ? responsibilityItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/responsibilities"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inInstitutions ? "is-open" : ""}`}
                      to="/institutions"
                      search={{ view: "checklist" }}
                    >
                      法规制度管理 <ChevronRight size={12} />
                    </Link>
                    {inInstitutions
                      ? institutionItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/institutions"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inPerformance ? "is-open" : ""}`}
                      to="/performance"
                      search={{ view: "indicators" }}
                    >
                      安全绩效考核 <ChevronRight size={12} />
                    </Link>
                    {inPerformance
                      ? performanceItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/performance"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inMeetings ? "is-open" : ""}`}
                      to="/meetings"
                      search={{ view: "ledger" }}
                    >
                      会议管理 <ChevronRight size={12} />
                    </Link>
                    {inMeetings
                      ? meetingItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/meetings"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>

            <div
              className={`nav-group ${inEmergencyDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("emergency")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("emergency")}
              >
                <Flag size={17} />
                <span>应急与事故</span>
                {openDomains.has("emergency") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("emergency") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inEmergency ? "is-open" : ""}`}
                      to="/emergency"
                      search={{ view: "plans" }}
                    >
                      应急管理 <ChevronRight size={12} />
                    </Link>
                    {inEmergency
                      ? emergencyItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/emergency"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inAccidents ? "is-open" : ""}`}
                      to="/accidents"
                      search={{ view: "ledger" }}
                    >
                      事故事件管理 <ChevronRight size={12} />
                    </Link>
                    {inAccidents
                      ? accidentItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/accidents"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>

            <div
              className={`nav-group ${inPersonnelDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("training")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("training")}
              >
                <Bookmark size={17} />
                <span>培训与人员保障</span>
                {openDomains.has("training") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("training") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inTraining ? "is-open" : ""}`}
                      to="/training"
                      search={{ view: "plans" }}
                    >
                      教育培训管理 <ChevronRight size={12} />
                    </Link>
                    {inTraining
                      ? trainingItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/training"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inLicenses ? "is-open" : ""}`}
                      to="/licenses"
                      search={{ view: "enterprise" }}
                    >
                      资质证照管理 <ChevronRight size={12} />
                    </Link>
                    {inLicenses
                      ? licenseItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/licenses"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inContractors ? "is-open" : ""}`}
                      to="/contractors"
                      search={{ view: "access" }}
                    >
                      相关方管理 <ChevronRight size={12} />
                    </Link>
                    {inContractors
                      ? contractorItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/contractors"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inInvestment ? "is-open" : ""}`}
                      to="/investment"
                      search={{ view: "plans" }}
                    >
                      安全投入管理 <ChevronRight size={12} />
                    </Link>
                    {inInvestment
                      ? investmentItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/investment"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>

            <div
              className={`nav-group ${inAnalyticsDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("analytics")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("analytics")}
              >
                <BarChart3 size={17} />
                <span>统计与档案</span>
                {openDomains.has("analytics") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("analytics") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inAnalytics ? "is-open" : ""}`}
                      to="/analytics"
                      search={{ view: "domains" }}
                    >
                      统计分析 <ChevronRight size={12} />
                    </Link>
                    {inAnalytics
                      ? analyticsItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/analytics"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inArchives ? "is-open" : ""}`}
                      to="/archives"
                      search={{ view: "ledger" }}
                    >
                      归档管理 <ChevronRight size={12} />
                    </Link>
                    {inArchives
                      ? archiveItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view ? "is-current" : ""}`}
                            key={view}
                            to="/archives"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>

            <div
              className={`nav-group ${inOrganizationDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("organization")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("organization")}
              >
                <Layers3 size={17} />
                <span>组织与配置</span>
                {openDomains.has("organization") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("organization") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inOrganization ? "is-open" : ""}`}
                      to="/organization"
                      search={{ view: "units" }}
                    >
                      组织架构管理 <ChevronRight size={12} />
                    </Link>
                    {inOrganization
                      ? organizationItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "units") ? "is-current" : ""}`}
                            key={view}
                            to="/organization"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inConfiguration ? "is-open" : ""}`}
                      to="/configuration"
                      search={{ view: "dictionaries" }}
                    >
                      基础配置管理 <ChevronRight size={12} />
                    </Link>
                    {inConfiguration
                      ? configurationItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "dictionaries") ? "is-current" : ""}`}
                            key={view}
                            to="/configuration"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>

            <div
              className={`nav-group ${inIntegrationDomain ? "is-active" : ""}`}
            >
              <button
                aria-expanded={openDomains.has("connectors")}
                className="nav-group__title"
                type="button"
                onClick={() => toggleDomain("connectors")}
              >
                <BarChart3 size={17} />
                <span>集成与报送</span>
                {openDomains.has("connectors") ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </button>
              {openDomains.has("connectors") ? (
                <>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inIntegrations ? "is-open" : ""}`}
                      to="/integrations"
                      search={{ view: "comprehensive" }}
                    >
                      平台对接管理 <ChevronRight size={12} />
                    </Link>
                    {inIntegrations
                      ? integrationItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "comprehensive") ? "is-current" : ""}`}
                            key={view}
                            to="/integrations"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                  <div className="nav-module">
                    <Link
                      className={`nav-module__title ${inSubmissions ? "is-open" : ""}`}
                      to="/submissions"
                      search={{ view: "monitor" }}
                    >
                      对外数据报送 <ChevronRight size={12} />
                    </Link>
                    {inSubmissions
                      ? submissionItems.map(([view, label]) => (
                          <Link
                            className={`nav-page ${currentView === view || (!currentView && view === "monitor") ? "is-current" : ""}`}
                            key={view}
                            to="/submissions"
                            search={{ view }}
                          >
                            <span />
                            {label}
                          </Link>
                        ))
                      : null}
                  </div>
                </>
              ) : null}
            </div>
          </nav>
          <div className="safety-branding">
            <div className="safety-branding__glow" />
            <img alt="安全护航" src="/assets/shield.png" />
            <strong>安全生产 · 防患未然</strong>
            <small>已平稳运行 268 天</small>
          </div>
        </aside>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
